import type { Addon, Item, LunchMenuCatalog } from "../../../api/menu";
import type { LunchCounters } from "../../../lib/lunchCatalog";
import type { PortionCartLine } from "../../../lib/lunchPortions";
import type { AddonSelection } from "../components/menu/useAddonSelection";
import { hiddenLunchAddonOptions, lunchRequiredAddonSoldOut } from "./useHiddenLunchItems";
import type { useLunchLimits } from "./useLunchLimits";

interface LunchLineLimitsOptions {
  lunchLimits: ReturnType<typeof useLunchLimits>;
  allAddons: Addon[];
  counters: LunchCounters | undefined;
  lunchMenus: LunchMenuCatalog[] | undefined;
  catalogIds: ReadonlySet<number>;
  orderItems: PortionCartLine[];
}

/**
 * Порциите от дневно меню в „Промени" (EditModifiersModal) — едно място за
 * трите случая: нов ред (с кошницата), повторна поръчка (без кошницата — отива
 * направо в сметката) и редакция на ред (кошницата БЕЗ самия ред).
 */
export const useLunchLineLimits = ({
  lunchLimits,
  allAddons,
  counters,
  lunchMenus,
  catalogIds,
  orderItems,
}: LunchLineLimitsOptions) => {
  const propsFor = (item: Item | null | undefined, baseCart: PortionCartLine[]) => ({
    hiddenAddonOptionIds: hiddenLunchAddonOptions(item, allAddons, counters, lunchLimits.usageOf(baseCart)),
    lunchMaxQuantityOf: (addons: AddonSelection) => lunchLimits.maxLineQuantity(item, addons, baseCart),
    lunchOptionRoomOf: (optionId: number) => lunchLimits.optionRoomOf(item, optionId, baseCart),
    lunchMenuName: lunchMenus?.find((menu) => menu.id === item?.lunchMenuId)?.name,
  });

  return {
    forNewLine: (item: Item | null | undefined, reorder: boolean) => propsFor(item, reorder ? [] : orderItems),
    forEditedLine: (item: Item | null | undefined, indices: number[]) =>
      propsFor(item, orderItems.filter((_, index) => !indices.includes(index))),
    /** „+" на копие, чиято задължителна добавка е изчерпана — казва го и не отваря „Промени". */
    refuseRequiredSoldOut: (item: Item | undefined) => {
      if (!item || !lunchRequiredAddonSoldOut(item, allAddons, catalogIds, counters, lunchLimits.cartUsage))
        return false;
      lunchLimits.warn(item.sourceItemId ?? item.id, 0);
      return true;
    },
  };
};
